import { useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useProject,
  usePipelineStatus,
  useArtifacts,
  useProjectCosts,
  useWorkspaceSettings,
} from "@/hooks/useApi";
import { ProjectHeaderBanner } from "@/components/projects/ProjectHeaderBanner";
import { AgentWorkflowDAG } from "@/components/projects/AgentWorkflowDAG";
import { SummaryCards } from "@/components/projects/SummaryCards";
import { costModelsLabel } from "@/lib/model-label";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useTranslation } from "react-i18next";

export default function ProjectDetail() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const { data: project, isLoading: projectLoading } = useProject(projectId);
  const { data: pipeline } = usePipelineStatus(projectId);
  const { data: artifacts = [] } = useArtifacts(projectId);
  const { data: costs } = useProjectCosts(projectId);
  const { data: workspace } = useWorkspaceSettings();

  useDocumentTitle(project?.name);

  if (projectLoading || !project) {
    return (
      <div className="-m-6">
        <Skeleton className="h-[260px] w-full rounded-none" />
        <div className="p-8 space-y-6">
          <Skeleton className="h-48 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-56" />
            <Skeleton className="h-56" />
            <Skeleton className="h-56" />
          </div>
        </div>
      </div>
    );
  }

  const agents = pipeline?.agents || [];
  // Sem cair no `current_phase`: ele nasce "discover" no banco e nada o
  // atualiza, então todo projeto parado aparecia como Discover.
  const computedPhase = pipeline?.computed_phase ?? null;

  return (
    <div className="-m-6">
      <ProjectHeaderBanner
        project={project}
        agents={agents}
        computedPhase={computedPhase}
        artifacts={artifacts}
        workspaceName={workspace?.workspace_name}
        totalTokens={costs?.total_tokens ?? 0}
        totalCost={costs?.total_cost ?? 0}
        modelLabel={costModelsLabel(costs?.models)}
        embeddingModelLabel={costs?.embedding_model_label}
      />

      <div className="p-8 space-y-7">
        <AgentWorkflowDAG agents={agents} projectId={projectId!} />

        <SummaryCards
          projectId={projectId!}
          project={project}
          artifacts={artifacts}
          totalCost={costs?.total_cost ?? 0}
          totalTokens={costs?.total_tokens ?? 0}
          modelLabel={costModelsLabel(costs?.models)}
          embeddingModelLabel={costs?.embedding_model_label}
        />

        <div className="text-center font-mono text-[10px] text-[#7b8799] tracking-[0.2em] pt-3 pb-4 uppercase">
          {t("Discover → Prepare → Explore → Realize → Deploy · Clean Core Guaranteed")}
        </div>
      </div>
    </div>
  );
}
